import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../api/axiosConfig";
import { toast } from "react-toastify";
import { useAuth } from "./AuthContext";

const WorkforceContext = createContext();
export const useWorkforce = () => useContext(WorkforceContext);

export const WorkforceProvider = ({ children }) => {
  const [workforce, setWorkforce] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    department: '',
    status: '',
    availability: '',
    search: '',
    page: 1,
    limit: 50
  });

  const { user } = useAuth();

  // Fetch workforce with filters
  const fetchWorkforce = useCallback(async (customFilters = {}) => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = { ...filters, ...customFilters };
      const queryString = new URLSearchParams(
        Object.entries(params).filter(([_, v]) => v !== '' && v !== null)
      ).toString();
      
      const res = await api.get(`/workforce?${queryString}`);
      setWorkforce(res.data.data || res.data || []);
      return res.data;
    } catch (err) {
      console.warn("Failed to load workforce:", err);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  // Fetch dashboard stats
  const fetchDashboardStats = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await api.get('/workforce/dashboard/stats');
      setDashboardStats(res.data);
      return res.data;
    } catch (err) {
      console.error("Failed to load dashboard stats", err);
    }
  }, [user]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await api.get('/workforce/analytics');
      setAnalytics(res.data);
      return res.data;
    } catch (err) {
      console.error("Failed to load analytics", err);
    }
  }, [user]);

  // Get single workforce by ID
  const getWorkforceById = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/workforce/${id}`);
      return res.data?.data || res.data;
    } catch (err) {
      toast.error("Failed to load employee details");
      throw err;
    }
  }, []);

  // Create workforce
  const addWorkforce = async (data) => {
    try {
      const res = await api.post('/workforce', data);
      await fetchWorkforce();
      toast.success("Employee onboarded successfully!");
      return res.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to add employee";
      toast.error(errorMsg);
      throw err;
    }
  };

  // Update workforce
  const updateWorkforce = async (id, data) => {
    try {
      const res = await api.put(`/workforce/${id}`, data);
      await fetchWorkforce();
      toast.success("Employee profile updated!");
      return res.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to update employee";
      toast.error(errorMsg);
      throw err;
    }
  };

  // Delete workforce
  const deleteWorkforce = async (id) => {
    if (!window.confirm("Are you sure you want to remove this employee?")) {
      return;
    }
    
    try {
      await api.delete(`/workforce/${id}`);
      setWorkforce(prev => prev.filter(w => w.id !== id));
      toast.success("Employee removed successfully");
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to delete employee";
      toast.error(errorMsg);
    }
  };

  // ============================================
  // ALLOCATION MANAGEMENT
  // ============================================

  const allocateToProject = async (data) => {
    try {
      const res = await api.post('/workforce/allocate', data);
      await fetchWorkforce();
      toast.success("Allocation successful!");
      return res.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Allocation failed";
      toast.error(errorMsg);
      throw err;
    }
  };

  const updateAllocation = async (id, data) => {
    try {
      const res = await api.put(`/workforce/allocations/${id}`, data);
      await fetchWorkforce();
      toast.success("Allocation updated!");
      return res.data;
    } catch (err) {
      toast.error("Failed to update allocation");
      throw err;
    }
  };

  const deleteAllocation = async (id) => {
    if (!window.confirm("Remove this allocation?")) {
      return;
    }
    
    try {
      await api.delete(`/workforce/allocations/${id}`);
      await fetchWorkforce();
      toast.success("Allocation removed");
    } catch (err) {
      toast.error("Failed to remove allocation");
    }
  };

  // ============================================
  // LEAVE MANAGEMENT
  // ============================================

  const getLeaveRequests = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const res = await api.get(`/workforce/leaves/requests?${queryString}`);
      return res.data;
    } catch (err) {
      toast.error("Failed to load leave requests");
      throw err;
    }
  };

  const createLeaveRequest = async (data) => {
    try {
      const res = await api.post('/workforce/leaves/request', data);
      toast.success("Leave request submitted!");
      return res.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Failed to submit leave request";
      toast.error(errorMsg);
      throw err;
    }
  };

  const updateLeaveRequest = async (id, data) => {
    try {
      const res = await api.put(`/workforce/leaves/${id}`, data);
      toast.success(data.status === 'Approved' ? "Leave approved!" : "Leave rejected");
      return res.data;
    } catch (err) {
      toast.error("Failed to update leave request");
      throw err;
    }
  };

  // ============================================
  // ATTENDANCE MANAGEMENT
  // ============================================

  const markAttendance = async (data) => {
    try {
      const res = await api.post('/workforce/attendance/mark', data);
      toast.success("Attendance marked!");
      return res.data;
    } catch (err) {
      toast.error("Failed to mark attendance");
      throw err;
    }
  };

  const getAttendance = async (params = {}) => {
    try {
      const queryString = new URLSearchParams(params).toString();
      const res = await api.get(`/workforce/attendance/records?${queryString}`);
      return res.data;
    } catch (err) {
      toast.error("Failed to load attendance");
      throw err;
    }
  };

  // Clear data if logged out
  useEffect(() => {
    if (!user || !user.id) {
      setWorkforce([]);
      setDashboardStats(null);
      setAnalytics(null);
    }
  }, [user]);

  const value = {
    // State
    workforce,
    dashboardStats,
    analytics,
    loading,
    filters,
    
    // Actions
    setFilters,
    fetchWorkforce,
    fetchDashboardStats,
    fetchAnalytics,
    getWorkforceById,
    addWorkforce,
    updateWorkforce,
    deleteWorkforce,
    
    // Allocations
    allocateToProject,
    updateAllocation,
    deleteAllocation,
    
    // Leave Management
    getLeaveRequests,
    createLeaveRequest,
    updateLeaveRequest,
    
    // Attendance
    markAttendance,
    getAttendance
  };

  return (
    <WorkforceContext.Provider value={value}>
      {children}
    </WorkforceContext.Provider>
  );
};